import { formatDate } from '@angular/common';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import {
  Clinic,
  FollowUpSelector,
  NutritionRecommendation,
  ProductRecommendationBack,
  RationingApiBody,
  SmartRecoApiBody,
} from '@app/core/models';
import { Market } from '@app/core/models/market';
import { RecommendationProgramApiBody } from '@app/core/models/program';
import { Helper, Programs, VetPreferences } from '@app/shared/utils';
import { formatNutritionRecommendation } from '@app/shared/utils/static-helpers/rationing-helper';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { BreedPetProfileModel, BreedPetProfileParameters, defaultLifestagesPlanDuration } from '../models/BreedController';
import { ApiService } from './../api.service';
import { environment } from '@env/environment';
import { formatNutritionalAssessment } from '@app/core/services/utils/nutrition-helper';
import { USER_SESSION_ANALYTICS_TOKEN } from '../../tracking';

@Injectable()
export class NutritionService extends ApiService {
  constructor(private http: HttpClient, @Inject(LOCALE_ID) protected localeId: string) {
    super();
  }

  createRecommendation(
    program: Programs,
    body: RationingApiBody,
    productIds: string[],
    withCups: boolean,
    isAdult: boolean
  ): Observable<NutritionRecommendation> {
    let params = new HttpParams();
    const parameters = {
      withCups,
      countryCode: VetPreferences.country.toLowerCase(),
      program: program,
    };

    const useDigitalRationingApi =
      isAdult && ![Programs.WEIGHT_LOSS, Programs.WEIGHT_STABILISATION_STEP_1, Programs.WEIGHT_STABILISATION_STEP_2].includes(program);
    const urlRationing = useDigitalRationingApi ? this.pathUrl.digitalRationing : this.pathUrl.rationing;
    const context: HttpContext = useDigitalRationingApi ? new HttpContext().set(USER_SESSION_ANALYTICS_TOKEN, true) : new HttpContext();
    Object.keys(parameters).forEach((key) => {
      params = params.append(key, parameters[key]);
    });
    const headersProductIds = productIds.join(';');

    const headers = new HttpHeaders({ product_id: headersProductIds });
    return this.http.post(urlRationing, body, { params, headers, context }).pipe(
      map((data) => formatNutritionRecommendation(data)),
      catchError(this.handleError.bind(this))
    );
  }

  getIBW(currentWeight: number, bcs: number): Observable<number> {
    let params = new HttpParams();
    params = params.set('bodyWeight', JSON.stringify(currentWeight));
    params = params.set('bcs', JSON.stringify(bcs));

    return this.http
      .get<{ idealBodyWeight: number }>(this.pathUrl.ibw, { params })
      .pipe(
        map((res) => res.idealBodyWeight),
        catchError(this.handleError.bind(this))
      );
  }

  checkIBWValidity(bcs: number, idealBodyWeight: number, bodyWeight: number): Observable<boolean> {
    let params = new HttpParams();
    params = params.set('bcs', JSON.stringify(bcs));
    params = params.set('idealBodyWeight', JSON.stringify(idealBodyWeight));
    params = params.set('bodyWeight', JSON.stringify(bodyWeight));

    return this.http
      .get<{ idealBodyWeightValidity: boolean }>(this.pathUrl.ibwCheck, { params })
      .pipe(
        map((res) => res.idealBodyWeightValidity),
        catchError(this.handleError.bind(this))
      );
  }

  recommendationPrograms(body: RecommendationProgramApiBody): Observable<FollowUpSelector> {
    return this.http.post<FollowUpSelector>(this.pathUrl.recommendationPrograms, body).pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * [GET]
   */
  getSmartRecoProducts(body: SmartRecoApiBody, clinic: Clinic, market: Market, limit?: number): Observable<any> {
    const parameters = {
      locale: Helper.catalogLang(this.localeId, market),
      countryCode: clinic?.companyAddress?.country.toLowerCase(),
    };
    if (limit) {
      parameters['limit'] = limit;
    }
    const params: HttpParams = this.params(parameters);
    const path = environment.isStandalone ? this.pathUrl.smartRecoProductsSrs() : this.pathUrl.smartRecoProducts(clinic?.id);
    return this.http.post<any>(path, body, { params });
  }

  /**
   * [GET]
   */
  getSmartRecoNutritionalAssessment(body: SmartRecoApiBody, clinic: Clinic, market: Market, limit?: number): Observable<any> {
    const parameters = {
      locale: Helper.catalogLang(this.localeId, market),
      countryCode: clinic?.companyAddress?.country.toLowerCase(),
    };
    const params = this.params(parameters);
    const path = this.pathUrl.nutritionalAssessment();
    return this.http
      .post<any>(path, body, { params })
      .pipe(
        tap((assessment) => {
          if (!assessment || assessment.macroNutrients?.length < 1 || assessment.otherNutrients?.length < 1) {
            catchError(this.handleError.bind(this));
          }
        }),
        map((data) => formatNutritionalAssessment(data)),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * [GET]
   */
  getSmartRecoNotRecommendedProduct(id: string, body: SmartRecoApiBody, clinic: Clinic): Observable<ProductRecommendationBack> {
    const parameters = {
      countryCode: clinic?.companyAddress?.country.toLowerCase(),
    };

    const params: HttpParams = this.params(parameters);
    return this.http
      .post<any>(this.pathUrl.smartRecoNotRecommendedProduct(id), body, { params })
      .pipe(catchError(this.handleError.bind(this)));
  }

  getBreedPetProfile = ({
    breedCode,
    dateOfBirth,
    genderCode,
    lifestagesPlanDuration,
  }: BreedPetProfileParameters): Observable<BreedPetProfileModel> =>
    this.http.get<BreedPetProfileModel>(this.pathUrl.petProfile(breedCode), {
      params: {
        ...(dateOfBirth && {
          dateOfBirth: formatDate(dateOfBirth, 'yyyy-MM-dd', this.localeId),
        }),
        ...(genderCode && { genderCode }),
        lifestagesPlanDuration: lifestagesPlanDuration?.toString() || defaultLifestagesPlanDuration,
      },
    });
}
